import { fetchPublicAuthConfig, type PublicAuthConfig } from './config';
import { isDemoMode } from '@/lib/demoMode';

/**
 * Boot-time head start for `GET /auth/config`.
 *
 * The runtime auth config gates the entire app: the Stytch provider cannot
 * mount until the backend names the project. Left to
 * `AuthConfigLoader`'s effect, that request only starts after the whole entry
 * bundle (Stytch, PostHog, the eager route tree, i18n) has evaluated
 * and React has mounted — hundreds of milliseconds of dead air before the
 * socket even opens.
 *
 * `startAuthConfigPrefetch()` is invoked from `./bootPrefetch`, imported first
 * in `main.tsx`, so the request is in flight while the rest of the bundle
 * evaluates. `loadPublicAuthConfig()` then adopts the in-flight promise instead
 * of issuing a second request.
 *
 * Deliberately NOT a cache: the response is fetched `no-store` on every boot so
 * a project change takes effect on the very next page load. This
 * only moves the request earlier in the same page load; it never reuses a
 * result across loads.
 */
let inFlight: Promise<PublicAuthConfig> | null = null;

/** Fire the config request as early as module evaluation allows. Idempotent. */
export function startAuthConfigPrefetch(): void {
  // Demo mode short-circuits to a static config and never calls the backend.
  if (isDemoMode() || inFlight) return;
  const request = fetchPublicAuthConfig();
  inFlight = request;
  // Nothing awaits this until React mounts. Swallow on a derived promise so a
  // boot-time network failure can't surface as an unhandled rejection; the
  // original promise keeps its rejection for whoever consumes it.
  void request.catch(() => undefined);
}

/**
 * Resolve the runtime auth config, adopting the prefetched request when one is
 * still unclaimed. Every later call (e.g. the loader's Retry button) issues a
 * fresh request, so a failed boot is never replayed from a stale promise.
 */
export function loadPublicAuthConfig(): Promise<PublicAuthConfig> {
  const prefetched = inFlight;
  inFlight = null;
  return prefetched ?? fetchPublicAuthConfig();
}
